import React, { useState } from 'react';
import { useStore, Workspace } from '../../stores';
import { ChevronDown, Plus, Settings, Users, Check, Building2 } from 'lucide-react';
import { cn } from '../../lib/utils';

interface WorkspaceSelectorProps {
  workspaces: Workspace[];
  onCreateWorkspace: () => void;
  onManageWorkspace: (workspaceId: string) => void;
  onManageMembers: (workspaceId: string) => void;
}

export function WorkspaceSelector({
  workspaces,
  onCreateWorkspace,
  onManageWorkspace,
  onManageMembers,
}: WorkspaceSelectorProps) {
  const { activeWorkspaceId, setActiveWorkspace } = useStore();
  const [isOpen, setIsOpen] = useState(false);

  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId);

  const handleSelect = (workspaceId: string) => {
    setActiveWorkspace(workspaceId);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-background border border-input rounded-md hover:bg-accent focus:outline-none focus:ring-2 focus:ring-ring transition-colors min-w-[160px]"
      >
        {activeWorkspace ? (
          <>
            <Building2 className="w-4 h-4 text-blue-500" />
            <span className="flex-1 text-left font-medium truncate max-w-[120px]">
              {activeWorkspace.name || 'Unnamed Workspace'}
            </span>
          </>
        ) : (
          <>
            <Building2 className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1 text-left text-muted-foreground">Select Workspace</span>
          </>
        )}
        <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-1 w-64 bg-card border border-border rounded-md shadow-lg z-50 overflow-hidden">
            {/* Active Workspace Header with Settings/Members */}
            {activeWorkspace && (activeWorkspace.role === 'owner' || activeWorkspace.role === 'admin') && (
              <>
                <div className="flex items-center gap-2 px-3 py-2">
                  <Building2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <span className="text-sm font-medium flex-1 truncate">{activeWorkspace.name || 'Unnamed Workspace'}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onManageMembers(activeWorkspace.id);
                      setIsOpen(false);
                    }}
                    className="p-1 rounded hover:bg-accent transition-colors"
                    title="Manage members"
                  >
                    <Users className="w-4 h-4 text-muted-foreground" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onManageWorkspace(activeWorkspace.id);
                      setIsOpen(false);
                    }}
                    className="p-1 rounded hover:bg-accent transition-colors"
                    title="Workspace settings"
                  >
                    <Settings className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
                <div className="h-px bg-border" />
              </>
            )}

            {/* Workspaces Header */}
            <div className="px-3 py-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Workspaces
              </p>
            </div>

            <div className="max-h-60 overflow-y-auto">
              {workspaces.length === 0 ? (
                <p className="px-3 py-2 text-sm text-muted-foreground">No workspaces yet</p>
              ) : (
                workspaces.map((workspace) => (
                  <div
                    key={workspace.id}
                    onClick={() => handleSelect(workspace.id)}
                    className="group flex items-center gap-2 px-3 py-2 hover:bg-accent cursor-pointer"
                  >
                    <Building2 className={cn(
                      'w-4 h-4 flex-shrink-0',
                      workspace.id === activeWorkspaceId ? 'text-blue-500' : 'text-muted-foreground'
                    )} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{workspace.name || 'Unnamed Workspace'}</p>
                      <p className="text-xs text-muted-foreground capitalize">{workspace.role || 'member'}</p>
                    </div>
                    {workspace.id === activeWorkspaceId && (
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    )}
                    {/* Hover actions for other workspaces (admins/owners only) */}
                    {workspace.id !== activeWorkspaceId && (workspace.role === 'owner' || workspace.role === 'admin') && (
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onManageMembers(workspace.id);
                            setIsOpen(false);
                          }}
                          className="p-1 rounded hover:bg-background"
                          title="Manage members"
                        >
                          <Users className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onManageWorkspace(workspace.id);
                            setIsOpen(false);
                          }}
                          className="p-1 rounded hover:bg-background"
                          title="Workspace settings"
                        >
                          <Settings className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            <div className="h-px bg-border" />
            <button
              onClick={() => {
                onCreateWorkspace();
                setIsOpen(false);
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-primary hover:bg-accent transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Workspace
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default WorkspaceSelector;
